// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { Button, ImprCheckbox, ImprSwitch, Select } from '@imprivata-cloud/components';
import { Form } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Security.less';
import { UndoOutlined } from '@ant-design/icons';
import { Entitlement } from '@api/constants';
import { hasEntitlement } from '@api/utils';
import { useSession } from '@common/authorization/context/Session';
import { withRole } from '@common/authorization/Roles';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import {
  AuthnLevel,
  FieldNames,
  type FormValues,
  UserCanShowLiveFeedValue,
} from '@portal/containers/security/constants';
import { useSecurityData } from '@portal/containers/security/useSecurityData';
import FaceBioIcon from '../../../assets/icons/authenticators/face_bio.svg?react';
import IidIcon from '../../../assets/icons/authenticators/imprivata_ID.svg?react';
import PasswordIcon from '../../../assets/icons/authenticators/password.svg?react';
import DoubleArrowIcon from '../../../assets/icons/double-arrow.svg?react';
import { featureSupported } from '@/featureFlagsUtils';
import { ROLE_TYPE_ADMIN } from '@portal/constants';

const TRANSLATION = 'portal.security';
const ENABLE_EPCS = featureSupported('ENABLE_EPCS');

const authnLevelToLabelKey = (value: string): string => {
  switch (value) {
    case AuthnLevel.ICP_SINGLE_FA:
      return `${TRANSLATION}.options.ICP_SINGLE_FA`;
    case AuthnLevel.ICP_MFA:
      return `${TRANSLATION}.options.ICP_MFA`;
    case AuthnLevel.ICP_PASSWORDLESS_MFA:
      return `${TRANSLATION}.options.ICP_PASSWORDLESS_MFA`;
    default:
      return value;
  }
};

function Security() {
  const { t } = useTranslation();
  const { tenantInfo } = useSession();
  const enableEpcs = ENABLE_EPCS && hasEntitlement(tenantInfo, Entitlement.EPCS);
  const hasFaceRecognition = hasEntitlement(tenantInfo, Entitlement.FACE_RECOGNITION);

  const [form] = Form.useForm<FormValues>();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const ussSelected = Form.useWatch(FieldNames.USS_VALUE, form);
  const iamSelected = Form.useWatch(FieldNames.IAM_VALUE, form);
  const userCanShowLiveFeed =
    Form.useWatch(FieldNames.USER_CAN_SHOW_LIVE_FEED_VALUE, form) ?? false;

  const {
    ussAuthnStrength,
    iamAuthnStrength,
    ussAuthnStrengthError,
    iamAuthnStrengthError,
    ussSetting,
    iamSetting,
    userCanShowLiveFeedSetting,
    isLoading,
    refetchAll,
    syncAuthnStrengthCache,
    updateSettings,
  } = useSecurityData();

  // Populate a dropdown + one-factor checkbox pair from the server's current
  // authentication-strength value. Takes the value (not the full response) so
  // the populating effects can depend on it alone and avoid re-running when
  // unrelated parts of the response change.
  const applyAuthnStrengthToForm = useCallback(
    (
      currentValue: string | undefined,
      valueField: FieldNames,
      oneFactorField: FieldNames,
    ) => {
      if (!currentValue) {
        return;
      }
      const isMfaIfEnrolled = currentValue === AuthnLevel.ICP_MFA_IF_ENROLLED;
      form.setFieldsValue({
        [valueField]: (isMfaIfEnrolled ? AuthnLevel.ICP_MFA : currentValue) as AuthnLevel,
        [oneFactorField]: isMfaIfEnrolled,
      });
    },
    [form],
  );

  // Populate USS form from authentication-strength currentValue
  useEffect(() => {
    applyAuthnStrengthToForm(
      ussAuthnStrength?.currentValue,
      FieldNames.USS_VALUE,
      FieldNames.USS_ONE_FACTOR_VALUE,
    );
  }, [ussAuthnStrength?.currentValue, applyAuthnStrengthToForm]);

  // Populate IAM form from authentication-strength currentValue
  useEffect(() => {
    applyAuthnStrengthToForm(
      iamAuthnStrength?.currentValue,
      FieldNames.IAM_VALUE,
      FieldNames.IAM_ONE_FACTOR_VALUE,
    );
  }, [iamAuthnStrength?.currentValue, applyAuthnStrengthToForm]);

  // Populate the user-can-show-live-feed switch from the loaded setting
  useEffect(() => {
    form.setFieldsValue({
      [FieldNames.USER_CAN_SHOW_LIVE_FEED_VALUE]: userCanShowLiveFeedSetting?.value
        ? userCanShowLiveFeedSetting.value.toLowerCase() === UserCanShowLiveFeedValue.ON
        : true,
    });
  }, [userCanShowLiveFeedSetting, form]);

  const onFormChanges = (changedValues: FormValues) => {
    setIsFormDirty(true);
    if (
      changedValues[FieldNames.USS_VALUE] &&
      changedValues[FieldNames.USS_VALUE] !== AuthnLevel.ICP_MFA
    ) {
      form.setFieldValue(FieldNames.USS_ONE_FACTOR_VALUE, false);
    }

    if (
      changedValues[FieldNames.IAM_VALUE] &&
      changedValues[FieldNames.IAM_VALUE] !== AuthnLevel.ICP_MFA
    ) {
      form.setFieldValue(FieldNames.IAM_ONE_FACTOR_VALUE, false);
    }
  };

  function onFinish(formValues: FormValues) {
    clearErrorNotifications();
    updateSettings(
      { ussSetting, iamSetting, userCanShowLiveFeedSetting, formValues },
      {
        onSuccess: async () => {
          syncAuthnStrengthCache(formValues);
          await refetchAll();
          setIsFormDirty(false);
        },
        onError: (e: ApiError) => {
          emitError(new AppError(e));
        },
      },
    );
  }

  async function handleUndo() {
    clearErrorNotifications();
    await refetchAll();
    applyAuthnStrengthToForm(
      ussAuthnStrength?.currentValue,
      FieldNames.USS_VALUE,
      FieldNames.USS_ONE_FACTOR_VALUE,
    );
    applyAuthnStrengthToForm(
      iamAuthnStrength?.currentValue,
      FieldNames.IAM_VALUE,
      FieldNames.IAM_ONE_FACTOR_VALUE,
    );
    setIsFormDirty(false);
  }

  const ussFilteredOptions = useMemo(
    () =>
      (ussAuthnStrength?.availableValues ?? [])
        .filter(value => value !== AuthnLevel.ICP_MFA_IF_ENROLLED)
        .map(value => ({ value, label: t(authnLevelToLabelKey(value)) })),
    [ussAuthnStrength?.availableValues, t],
  );
  const iamFilteredOptions = useMemo(
    () =>
      (iamAuthnStrength?.availableValues ?? [])
        .filter(value => value !== AuthnLevel.ICP_MFA_IF_ENROLLED)
        .map(value => ({ value, label: t(authnLevelToLabelKey(value)) })),
    [iamAuthnStrength?.availableValues, t],
  );

  const ussOptionsBroken = !!ussAuthnStrengthError || ussFilteredOptions.length === 0;
  const iamOptionsBroken = !!iamAuthnStrengthError || iamFilteredOptions.length === 0;

  return (
    <div className={'security-container'}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={onFormChanges}
        aria-label="security-level-form"
        initialValues={{ [FieldNames.USER_CAN_SHOW_LIVE_FEED_VALUE]: true }}
      >
        <p>{t(`${TRANSLATION}.description`)}</p>
        <Form.Item name={FieldNames.USS_VALUE}>
          <Select
            label={t(`${TRANSLATION}.uss-field-label`)}
            aria-label="My-Imprivata-Identity-AuthN-level"
            autoFocus
            options={ussFilteredOptions}
            disabled={isLoading || ussOptionsBroken}
          />
        </Form.Item>
        <Form.Item name={FieldNames.USS_ONE_FACTOR_VALUE} valuePropName="checked">
          {/*  When checked, server value must be icp-mfa-if-enrolled */}
          <ImprCheckbox disabled={ussSelected !== AuthnLevel.ICP_MFA}>
            <span className="body-alt">{t(`${TRANSLATION}.checkbox-label`)}</span>
          </ImprCheckbox>
        </Form.Item>
        <hr />
        <Form.Item name={FieldNames.IAM_VALUE}>
          <Select
            label={t(`${TRANSLATION}.iam-field-label`)}
            aria-label="Imprivata-Access-Management-AuthN-level"
            options={iamFilteredOptions}
            disabled={isLoading || iamOptionsBroken}
          />
        </Form.Item>
        <Form.Item name={FieldNames.IAM_ONE_FACTOR_VALUE} valuePropName="checked">
          {/*  When checked, server value must be icp-mfa-if-enrolled */}
          <ImprCheckbox disabled={iamSelected !== AuthnLevel.ICP_MFA}>
            <span className="body-alt">{t(`${TRANSLATION}.checkbox-label`)}</span>
          </ImprCheckbox>
        </Form.Item>
        {enableEpcs && (
          <>
            <hr />
            <Form.Item>
              <Select
                label={t(`${TRANSLATION}.epcs-field-label`)}
                aria-label="EPCS-AuthN-level"
                value={AuthnLevel.ICP_MFA}
                options={[
                  { value: AuthnLevel.ICP_MFA, label: t(`${TRANSLATION}.epcs-mfa-option`) },
                ]}
                disabled
              />
            </Form.Item>
          </>
        )}
        {hasFaceRecognition && (
          <>
            <hr />
            <div className="face-auth-video-stream">
              <h3>{t(`${TRANSLATION}.face-auth-video-stream.title`)}</h3>
              <p>{t(`${TRANSLATION}.face-auth-video-stream.description`)}</p>
              <Form.Item name={FieldNames.USER_CAN_SHOW_LIVE_FEED_VALUE} valuePropName="checked">
                <ImprSwitch
                  aria-label={t(`${TRANSLATION}.face-auth-video-stream.aria-label`)}
                  disabled={isLoading}
                  isChecked={userCanShowLiveFeed}
                  switchLabel={{
                    labelOn: t(`${TRANSLATION}.face-auth-video-stream.label-on`),
                    labelOff: t(`${TRANSLATION}.face-auth-video-stream.label-off`),
                  }}
                />
              </Form.Item>
            </div>
          </>
        )}

        <div className="buttons-container">
          <Button
            type="text"
            className="text-button"
            onClick={handleUndo}
            disabled={!isFormDirty}
            icon={<UndoOutlined className="undo-icon" />}
          >
            {t('common.undo')}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={isLoading || !isFormDirty || ussOptionsBroken || iamOptionsBroken}
          >
            {t('common.save')}
          </Button>
        </div>
      </Form>
      <div className="guidelines">
        <h3>{t(`${TRANSLATION}.guidelines.header`)}</h3>
        <div className="flex-content">
          <div className="scale">
            <div className="rotated">
              <div>{t(`${TRANSLATION}.guidelines.scale-bottom`)}</div>
              <div>{t(`${TRANSLATION}.guidelines.scale-top`)}</div>
            </div>
            <div>
              <DoubleArrowIcon />
            </div>
          </div>
          <div>
            <div className="item-title">
              <div>{t(`${TRANSLATION}.guidelines.factors.passwordless.title`)}</div>
              <div>
                <PasswordIcon className="pale" />
                <FaceBioIcon />
                <IidIcon />
              </div>
            </div>
            <p>{t(`${TRANSLATION}.guidelines.factors.passwordless.desc`)}</p>
            <div className="item-title">
              <div>{t(`${TRANSLATION}.guidelines.factors.MFA.title`)}</div>
              <div>
                <PasswordIcon />
                <FaceBioIcon />
                <IidIcon />
              </div>
            </div>
            <p>{t(`${TRANSLATION}.guidelines.factors.MFA.desc`)}</p>
            <div className="item-title">
              <div>{t(`${TRANSLATION}.guidelines.factors.single.title`)}</div>
              <div>
                <PasswordIcon />
                <FaceBioIcon className="pale" />
                <IidIcon className="pale" />
              </div>
            </div>
            <p>{t(`${TRANSLATION}.guidelines.factors.single.desc`)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(Security);
